import { useEffect, useMemo, useRef, useState, useCallback, memo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { debounce } from "lodash";
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { ArrowUpDown, X, Download, Upload, RefreshCcw, AlertCircle, Columns, ChevronDown, Settings2 } from "lucide-react";
import ColumnSettingsModal, { type ColumnDef, type ColumnSettingsValue } from "@/components/gap-management/shared/TableColumnSettings";
import Dropzone from "@/components/gap-management/shared/MeasureEvidenceFileUploader";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { isFeatureEnabled } from "@/config/features";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";
import userStorage from "@/lib/userStorage";
import { Switch } from "@/components/ui/switch";
import ManageViewsModal from "@/components/gap-management/shared/TableViewsManager";

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import RichTextEditor from "@/components/gap-management/shared/MeasureNotesEditor";
import { Separator } from "@/components/ui/separator";
import StatusBubble from "@/components/gap-management/quality/PatientStatusIndicator";
import { getPatientRows, updatePatientOutcome, batchUpdatePatientOutcomes, checkJobStatus, getProviders, type Provider } from "@/lib/measure-outcomes";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { YearLockBanner } from "@/components/gap-management/shared/YearLockBanner";
import { useAuth } from "@/contexts/AuthContext";
import { useConflictResolutionFeedback } from "@/components/shared/ConflictNotificationSystem";

// Standardized badge colors for Navy Clinical Gentle theme (lighter variant)
const BADGE_COLORS = {
  MIPS_CQM: 'bg-[#5a7a9f] text-white border-[#4a6a8f]',
  QCDR: 'bg-[#5a7a9f] text-white border-[#4a6a8f]',
  PROCESS: 'bg-[#7a9bac] text-white border-[#6a8b9c]',
  OUTCOME: 'bg-[#8aabbc] text-white border-[#7a9bac]',
  INT_OUTCOME: 'bg-[#8aabbc] text-white border-[#7a9bac]',
  SEVEN_PT_CAP: 'bg-[#7a9aaa] text-white border-[#6a8a9a]',
  INVERSE: 'bg-[#6d9f9f] text-white border-[#5d8f8f]',
  BONUS: 'bg-[#3b82f6] text-white border-[#2563eb]', // Using Tailwind's blue-500 for hover-like effect
  eCQM: 'bg-[#7a9bac] text-white border-[#6a8b9c]',
  DEFAULT: 'bg-gray-400 text-white border-gray-500'
};

// Dynamic color palette for stratum differentiation - Navy Clinical Gentle theme
const stratumColorPalette = [
  BADGE_COLORS.MIPS_CQM,
  BADGE_COLORS.PROCESS,
  BADGE_COLORS.OUTCOME,
  BADGE_COLORS.INVERSE,
  BADGE_COLORS.eCQM,
  BADGE_COLORS.SEVEN_PT_CAP,
  BADGE_COLORS.BONUS,
  'bg-[#8aabbc] text-white border-[#7a9bac]',
  'bg-[#9abbcc] text-white border-[#8aabbc]',
  'bg-[#aacbdc] text-white border-[#9abbcc]'
];

// Helper function to get patient display name with intelligent fallback
const getPatientDisplayName = (row: PatientRow | undefined): string => {
  if (!row) return 'Patient Details';
  
  const firstName = row.firstName ? row.firstName.trim() : '';
  const lastName = row.lastName ? row.lastName.trim() : '';
  const fullName = row.fullName ? row.fullName.trim() : '';
  
  // If all three are provided, prefer fullName
  if (firstName && lastName && fullName) {
    return fullName;
  }
  
  // If firstName and lastName exist, use them
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  
  // If firstName/lastName are empty but fullName exists, use fullName
  if (fullName) {
    return fullName;
  }
  
  // If only one name part exists, use it
  if (firstName) return firstName;
  if (lastName) return lastName;
  
  // Last resort: Fall back to Patient ID
  return `Patient ${row.pid}`;
};

// Component to display stratum with dynamic color coding
const StratumBadge = ({ stratum, allStrata }: { stratum?: string; allStrata: string[] }) => {
  if (!stratum) {
    return (
      <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">
        N/A
      </Badge>
    );
  }
  
  // Get unique strata for consistent color assignment
  const uniqueStrata = [...new Set(allStrata.filter(Boolean))].sort();
  const stratumIndex = uniqueStrata.indexOf(stratum);
  const colorClass = stratumColorPalette[stratumIndex % stratumColorPalette.length];
  
  return (
    <Badge variant="outline" className={`${colorClass} font-medium`}>
      {stratum}
    </Badge>
  );
};

interface EvidenceFile {
  name: string;
  type: string;
  size: number;
  dataUrl: string;
  uploadedAt: number;
}

interface PatientRow {
  pid: string;
  encounterId?: string; // Unique encounter identifier
  evaluationType?: string; // GROUP or NPI
  ip: boolean;
  ex: boolean; // Denominator Exclusion
  den: boolean; // Denominator
  met: boolean; // Numerator Met
  exc: boolean; // Numerator Exception
  not: boolean; // Numerator Not Met
  source: string;
  notes: string;
  notesRich?: string;
  firstName: string;
  lastName: string;
  fullName?: string; // Optional full name field
  dob: string; // YYYY-MM-DD
  gender: string; // M/F/Other
  npi: string;
  npiName: string;
  dos: string; // Date of service YYYY-MM-DD
  cptCodes: string; // comma-separated
  cptModifiers: string;
  icd10Codes: string; // comma-separated
  placeOfService: string;
  stratum?: string;
  evidence?: EvidenceFile[];
}

interface ActivityLogEntry {
  version: number;
  change_timestamp: string;
  change_type: string;
  changed_by: string;
  batch_name?: string;
  processing_notes?: string;
  change_description?: string;
}

// Memoized Patient Row Component for Performance
interface PatientTableRowProps {
  row: PatientRow;
  index: number;
  originalIdx: number;
  visibleOrder: string[];
  colSettings: any;
  allStrata: string[];
  onToggle: (idx: number, field: keyof PatientRow) => void;
  onOpenDetail: (idx: number) => void;
  locked?: boolean;
}

const PatientTableRow = memo(({ 
  row, 
  index, 
  originalIdx,
  visibleOrder,
  colSettings,
  allStrata,
  onToggle,
  onOpenDetail,
  locked = false
}: PatientTableRowProps) => {
  return (
    <TableRow className={cn(
      "hover:bg-muted/50 transition-colors",
      index % 2 === 0 ? "bg-background" : "bg-muted/20"
    )}>
      {visibleOrder.map((key) => {
        let content: any = null;
        switch (key) {
          case 'pid':
            content = (
              <Button variant="link" className="h-auto p-0 text-base font-bold text-primary hover:text-primary/80" onClick={() => onOpenDetail(originalIdx)} aria-label={`Open details for ${row.pid}`}>
                {row.pid}
              </Button>
            );
            break;
          case 'fullName':
            // Use the existing getPatientName function to get the full name
            const fullName = row.fullName || 
              (row.firstName && row.lastName ? `${row.firstName} ${row.lastName}` : 
               row.firstName || row.lastName || `Patient ${row.pid}`);
            content = fullName;
            break;
          case 'first':
            content = row.firstName;
            break;
          case 'last':
            content = row.lastName;
            break;
          case 'dob':
            content = row.dob;
            break;
          case 'gender':
            content = row.gender;
            break;
          case 'npiName':
            content = row.npiName;
            break;
          case 'stratum':
            content = (
              <StratumBadge 
                stratum={row.stratum} 
                allStrata={allStrata} 
              />
            );
            break;
          case 'dos':
            content = row.dos;
            break;
          case 'npi':
            content = row.npi;
            break;
          case 'cptCodes':
            content = row.cptCodes;
            break;
          case 'cptModifiers':
            content = row.cptModifiers;
            break;
          case 'icd10Codes':
            content = row.icd10Codes;
            break;
          case 'placeOfService':
            content = row.placeOfService;
            break;
          case 'ip':
            content = (
              <div className="flex justify-center py-1">
                <StatusBubble
                  status="ip"
                  active={row.ip}
                  disabled
                  hasEvidence={(row.evidence?.length ?? 0) > 0}
                  label="Initial Population"
                  timestamp={row.dos}
                  source={row.source}
                  onOpenDetail={() => onOpenDetail(originalIdx)}
                />
              </div>
            );
            break;
          case 'ex':
            content = (
              <div className="flex justify-center py-1">
                <StatusBubble
                  status="ex"
                  active={row.ex}
                  disabled={locked}
                  hasEvidence={(row.evidence?.length ?? 0) > 0}
                  label="Denominator Exclusion"
                  timestamp={row.dos}
                  source={row.source}
                  onClick={() => onToggle(originalIdx, 'ex')}
                  onOpenDetail={() => onOpenDetail(originalIdx)}
                />
              </div>
            );
            break;
          case 'den':
            content = (
              <div className="flex justify-center py-1">
                <StatusBubble
                  status="den"
                  active={row.den}
                  disabled={locked || !row.ex}
                  hasEvidence={(row.evidence?.length ?? 0) > 0}
                  label="Denominator"
                  timestamp={row.dos}
                  source={row.source}
                  onClick={() => onToggle(originalIdx, 'den')}
                  onOpenDetail={() => onOpenDetail(originalIdx)}
                />
              </div>
            );
            break;
          case 'met':
            content = (
              <div className="flex justify-center py-1">
                <StatusBubble
                  status="met"
                  active={row.met}
                  disabled={locked || row.ex}
                  hasEvidence={(row.evidence?.length ?? 0) > 0}
                  label="Numerator Met"
                  timestamp={row.dos}
                  source={row.source}
                  onClick={() => onToggle(originalIdx, 'met')}
                  onOpenDetail={() => onOpenDetail(originalIdx)}
                />
              </div>
            );
            break;
          case 'exc':
            content = (
              <div className="flex justify-center py-1">
                <StatusBubble
                  status="exc"
                  active={row.exc}
                  disabled={locked || row.ex}
                  hasEvidence={(row.evidence?.length ?? 0) > 0}
                  label="Numerator Exception"
                  timestamp={row.dos}
                  source={row.source}
                  onClick={() => onToggle(originalIdx, 'exc')}
                  onOpenDetail={() => onOpenDetail(originalIdx)}
                />
              </div>
            );
            break;
          case 'not':
            content = (
              <div className="flex justify-center py-1">
                <StatusBubble
                  status="not"
                  active={row.not}
                  disabled={locked || row.ex}
                  hasEvidence={(row.evidence?.length ?? 0) > 0}
                  label="Numerator Not Met"
                  timestamp={row.dos}
                  source={row.source}
                  onClick={() => onToggle(originalIdx, 'not')}
                  onOpenDetail={() => onOpenDetail(originalIdx)}
                />
              </div>
            );
            break;
          case 'source':
            content = row.source;
            break;
          case 'notes':
            content = (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-1"
                onClick={() => onOpenDetail(originalIdx)}
              >
                {row.notes || row.notesRich ? (
                  <span className="text-xs truncate max-w-[100px]">{row.notes || 'View notes'}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">Add notes</span>
                )}
              </Button>
            );
            break;
        }
        return (
          <TableCell key={key} className={colSettings.tooltips ? "group relative" : ""}>
            {content}
            {colSettings.tooltips && <span className="hidden group-hover:inline-block absolute z-10 px-2 py-1 text-xs text-white bg-gray-800 rounded -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">{key}</span>}
          </TableCell>
        );
      })}
    </TableRow>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if data actually changed
  return (
    prevProps.row === nextProps.row &&
    prevProps.originalIdx === nextProps.originalIdx &&
    prevProps.index === nextProps.index &&
    prevProps.visibleOrder === nextProps.visibleOrder &&
    prevProps.colSettings === nextProps.colSettings &&
    prevProps.allStrata === nextProps.allStrata &&
    prevProps.locked === nextProps.locked
  );
});

PatientTableRow.displayName = 'PatientTableRow';

function toCsv(rows: any[], headers: string[]) {
  const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [headers.join(","), ...rows.map(r => headers.map(h => esc(r[h])).join(","))].join("\n");
}

const toBool = (v: any) => {
  const s = String(v ?? "").trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes";
};

interface MeasureDetailProps {
  measureIdFromUrl?: string;
  /**
   * When true (Patients tab By-measure lens), hide the Gap Management chrome
   * (back button, NPI lane switch) — Patients is GROUP-only by design and
   * already provides its own navigation shell.
   */
  embedded?: boolean;
  /**
   * Explicit practice target (Patients tab in APP Plus mode points the embed at
   * the ACO's shadow practice). Takes precedence over ?practiceId= and the
   * MIPS session selection.
   */
  practiceIdOverride?: string;
}

export default function MeasureDetail({ measureIdFromUrl, embedded = false, practiceIdOverride }: MeasureDetailProps) {
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const { id: routeId } = useParams();
  // Use prop from QualityWrapper if available, otherwise fall back to route param or default
  const id = measureIdFromUrl || routeId || "MIPS-110";
  
  // Debug: Confirm component is mounting
  console.log('[MeasureDetail] Component rendering with id:', id);
  console.log('[MeasureDetail] measureIdFromUrl:', measureIdFromUrl, 'routeId:', routeId);
  
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { user, selectedPractice } = useAuth();
  // ?practiceId= override (D12): APP Plus "View Details" points this page at the
  // ACO's shadow practice without touching the MIPS session selection.
  const selectedPracticeId = practiceIdOverride || searchParams.get('practiceId') || userStorage.getSessionItem('selectedPracticeId');
  
  console.log('[MeasureDetail] selectedPracticeId:', selectedPracticeId);
  const { handleOutcomeUpdateResponse } = useConflictResolutionFeedback();
  
  // Evaluation type from URL params. Embedded Patients lens is GROUP-only.
  const evaluationType = embedded
    ? 'GROUP'
    : ((searchParams.get('evaluationType') as 'GROUP' | 'NPI') || 'GROUP');
  const selectedNpi = embedded ? '' : (searchParams.get('npi') || '');

  // D12: when opened from APP Plus (shadow-practice override), back-navigation
  // stays inside the APP module instead of dumping the user into MIPS.
  const isAppContext = window.location.pathname.startsWith('/programs/app-plus');
  const backTo = isAppContext ? '/programs/app-plus/qps' : '/programs/mips/gap-management/quality';
  const backLabel = isAppContext ? 'Back to QPS Management' : 'Back to Gap Management';
  
  // Providers state for NPI mode
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  const [rows, setRows] = useState<PatientRow[]>([]);
  const [originalRows, setOriginalRows] = useState<PatientRow[]>([]); // Store original data for change comparison
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // For button loading state
  const [confirmButtonText, setConfirmButtonText] = useState("Confirm & Recalculate"); // Dynamic button text
  const [currentJobId, setCurrentJobId] = useState<string | null>(null); // Track current job
  const [hasImportedCSV, setHasImportedCSV] = useState(false); // Track if CSV was imported
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]); // Activity log data
  const [showAllActivityLog, setShowAllActivityLog] = useState(false); // Toggle for showing all activity log entries
  
  // Track changes as they happen for instant confirmation
  const changesMapRef = useRef<Map<string, any>>(new Map());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 100; // Fixed at 100 rows per page
  
  // API URL configuration
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  
  // Fetch providers when in NPI mode
  useEffect(() => {
    const fetchProvidersList = async () => {
      if (evaluationType !== 'NPI' || !selectedPracticeId) {
        return;
      }
      
      setLoadingProviders(true);
      try {
        const providersList = await getProviders();
        setProviders(providersList);
      } catch (error) {
        console.error('Error fetching providers:', error);
      } finally {
        setLoadingProviders(false);
      }
    };
    
    fetchProvidersList();
  }, [evaluationType, selectedPracticeId]);
  
  // Helper to update evaluation type in URL
  const setEvaluationType = (type: 'GROUP' | 'NPI') => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('evaluationType', type);
    if (type === 'GROUP') {
      newParams.delete('npi');
    } else if (type === 'NPI' && providers.length > 0 && !searchParams.get('npi')) {
      // Auto-select first NPI when switching to NPI mode
      newParams.set('npi', providers[0].npi);
    }
    setSearchParams(newParams);
  };
  
  const setSelectedNpi = (npi: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('npi', npi);
    setSearchParams(newParams);
  };
  
  // Fetch activity log for the current patient
  const fetchActivityLog = useCallback(async (patientId: string, measureId: string, encounterId?: string) => {
    if (!selectedPracticeId) return;
    
    try {
      let url = `${apiUrl}/api/performance/${selectedPracticeId}/patients/${patientId}/history?measureId=${measureId}`;
      if (encounterId) {
        url += `&encounterId=${encounterId}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setActivityLog(data);
      }
    } catch (error) {
      console.error('Error fetching activity log:', error);
    }
  }, [selectedPracticeId]);
  
  // Poll job status
  const pollJobStatus = useCallback(async (jobId: string) => {
    const checkInterval = setInterval(async () => {
      const job = await checkJobStatus(jobId);
      
      if (!job || job.status === 'completed' || job.status === 'failed') {
        clearInterval(checkInterval);
        setIsProcessing(false);
        setCurrentJobId(null);
        
        if (job && job.status === 'completed') {
          const [measureId, submissionMethod = 'MIPS CQM'] = id.split(':');
          const successCount = job.results?.success?.length || 0;
          
          // Show success feedback
          setConfirmButtonText("✓ Updated");
          toast({
            title: "Updates Completed",
            description: `${successCount} records updated successfully.`,
          });
          
          // Clear pending state
          sessionStorage.removeItem(`pendingState_${measureId}`);
          sessionStorage.removeItem('currentBatchJob');
          
          // Reset button after delay
          setTimeout(() => {
            setConfirmButtonText("Confirm & Recalculate");
          }, 2000);
          
          // Refresh data
          const evalContext = { evaluationType, npi: evaluationType === 'NPI' ? selectedNpi : undefined, year: programYear };
          const freshData = await getPatientRows(measureId, submissionMethod, evalContext);
          setRows(freshData as PatientRow[]);
          setOriginalRows(freshData as PatientRow[]);
          changesMapRef.current.clear();
          setHasImportedCSV(false); // Reset import flag
        } else if (job && job.status === 'failed') {
          setConfirmButtonText("Failed - Try Again");
          toast({
            title: "Update Failed",
            description: "The batch update failed. Please try again.",
            variant: "destructive",
          });
          
          setTimeout(() => {
            setConfirmButtonText("Confirm & Recalculate");
          }, 2000);
        }
      }
    }, 2000); // Check every 2 seconds
    
    return () => clearInterval(checkInterval);
  }, [id, toast]);
  
  // State for progressive loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadedRowsCount, setLoadedRowsCount] = useState(0);

  // Combined initialization effect with parallel fetching and progressive loading
  useEffect(() => {
    let isMounted = true;
    
    const initializeDetailPage = async () => {
      if (!selectedPracticeId) {
        console.warn('No practice selected, cannot load patient data');
        return;
      }

      const [measureId, submissionMethod = 'MIPS CQM'] = id.split(':');
      const pendingStateKey = `pendingState_${measureId}`;
      
      try {
        // Parallel fetch all initialization data
        const [pendingStateStr, jobInfoStr, gapsAdditions] = await Promise.all([
          Promise.resolve(sessionStorage.getItem(pendingStateKey)),
          Promise.resolve(sessionStorage.getItem('currentBatchJob')),
          Promise.resolve(localStorage.getItem("gaps:additions"))
        ]);
        
        // Check manual mode
        const additions = JSON.parse(gapsAdditions || "[]");
        const manualMeasure = additions.find((m: any) => m.id === id && m.mode === "manual");
        setIsManualEntry(!!manualMeasure);
        
        // Handle pending state restoration
      if (pendingStateStr) {
        const pendingState = JSON.parse(pendingStateStr);
        
          // Load first page immediately
          setRows(pendingState.rows);
          setLoadedRowsCount(pendingState.rows.length);
          
          // Then load rest of data in background
          setIsLoadingMore(true);
        const evalContext = { evaluationType, npi: evaluationType === 'NPI' ? selectedNpi : undefined, year: programYear };
        const fullData = await getPatientRows(measureId, submissionMethod, evalContext) as PatientRow[];
        
          if (isMounted) {
        const restoredRows = [
              ...pendingState.rows,
              ...fullData.slice(pendingState.rows.length)
        ];
        
        setRows(restoredRows);
            setOriginalRows(fullData);
            setIsLoadingMore(false);
            setLoadedRowsCount(restoredRows.length);
            console.log(`Restored pending state: ${pendingState.rows.length} rows from page 1, ${pendingState.totalRows} total rows`);
          }
          
          // Check for associated job
        if (jobInfoStr) {
          const jobInfo = JSON.parse(jobInfoStr);
          const job = await checkJobStatus(jobInfo.jobId);
          
          if (job && job.status === 'processing') {
            setIsProcessing(true);
            setConfirmButtonText("Processing...");
            setCurrentJobId(jobInfo.jobId);
            pollJobStatus(jobInfo.jobId);
          } else if (job && job.status === 'completed') {
            const successCount = job.results?.success?.length || 0;
            toast({
              title: "Updates Completed",
              description: `Your previous updates have been saved. ${successCount} records updated.`,
            });
            
            sessionStorage.removeItem(pendingStateKey);
            sessionStorage.removeItem('currentBatchJob');
            
            const evalContext2 = { evaluationType, npi: evaluationType === 'NPI' ? selectedNpi : undefined, year: programYear };
            const freshData = await getPatientRows(measureId, submissionMethod, evalContext2);
              if (isMounted) {
            setRows(freshData as PatientRow[]);
            setOriginalRows(freshData as PatientRow[]);
            changesMapRef.current.clear();
                setHasImportedCSV(false);
              }
          } else if (job && job.status === 'failed') {
            toast({
              title: "Previous Update Failed",
              description: "Your previous update failed. The changes are still shown but not saved.",
              variant: "destructive",
            });
            sessionStorage.removeItem('currentBatchJob');
          }
        }
        } else if (!manualMeasure) {
          // No pending state, not manual mode. The API returns the full row set
          // in one response, so commit it in ONE state update. (The old
          // "progressive" path set 100 rows, flipped isLoadingMore on — which
          // swapped the Import CSV button to "Loading…" and the pager to a
          // spinner — then 100 ms later set all rows and flipped it back: a
          // visible flash on every visit for zero benefit, since the data was
          // already in memory. Pagination keeps the DOM small regardless.)
          const evalContext3 = { evaluationType, npi: evaluationType === 'NPI' ? selectedNpi : undefined, year: programYear };
          const allData = await getPatientRows(measureId, submissionMethod, evalContext3);

          if (isMounted && allData) {
            setRows(allData as PatientRow[]);
            setOriginalRows(allData as PatientRow[]);
            setLoadedRowsCount(allData.length);
            setIsLoadingMore(false);
            changesMapRef.current.clear();
          }
      }
    } catch (error) {
        console.error("Error initializing detail page:", error);
        if (isMounted) {
          toast({
            title: "Error loading data",
            description: "Failed to load patient data. Please refresh the page.",
            variant: "destructive",
          });
        }
      }
    };

    initializeDetailPage();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [id, selectedPracticeId, pollJobStatus, toast, evaluationType, selectedNpi]);

  const [warnOpen, setWarnOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{ idx: number; field: keyof PatientRow } | null>(null);
  const ackKey = `measureDetail:warnAck:${id}`;
  // Load per-row evidence and merge into rows.
  // PHI hygiene: evidence maps are keyed by patient/encounter IDs, so they live
  // in sessionStorage ONLY (wiped on logout + tab close) — never localStorage,
  // which survives logout on shared machines. Legacy localStorage copies are
  // purged on first load.
  useEffect(() => {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith("measureEvidence:")) localStorage.removeItem(key);
      }
    } catch {}
    try {
      const saved = JSON.parse(sessionStorage.getItem(`measureEvidence:${id}`) || "{}");
      if (saved && typeof saved === "object") {
        setRows((prev) => prev.map((r) => ({ 
          ...r, 
          evidence: saved[r.encounterId || r.pid] || []  // Use encounter_id as key if available
        })));
      }
    } catch {}
  }, [id]);

  const persistEvidenceFrom = (list: PatientRow[]) => {
    const map: Record<string, EvidenceFile[]> = {};
    list.forEach((r) => {
      if (r.evidence && r.evidence.length) {
        // Use encounter_id as key if available for proper evidence tracking
        map[r.encounterId || r.pid] = r.evidence;
      }
    });
    try {
      sessionStorage.setItem(`measureEvidence:${id}`, JSON.stringify(map));
    } catch { /* quota — evidence chips are session-local convenience only */ }
  };

  const onEvidenceUploadRow = async (rowKey: string, files: FileList) => {
    // rowKey is either encounter_id (for Visit frequency) or pid
    const arr = Array.from(files);
    const reads = await Promise.all(
      arr.map(
        (f) =>
          new Promise<EvidenceFile>((resolve) => {
            const fr = new FileReader();
            fr.onload = () =>
              resolve({ name: f.name, type: f.type, size: f.size, dataUrl: String(fr.result), uploadedAt: Date.now() });
            fr.readAsDataURL(f);
          })
      )
    );
    setRows((prev) => {
      const next = prev.map((r) => {
        // Match by encounter_id first, fallback to pid
        const currentKey = r.encounterId || r.pid;
        return currentKey === rowKey ? { ...r, evidence: [...(r.evidence || []), ...reads] } : r;
      });
      persistEvidenceFrom(next);
      return next;
    });
  };

  const filesToFileList = (files: File[]) => {
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    return dt.files;
  };
  const onEvidenceUploadRowFiles = async (rowKey: string, files: File[]) => {
    await onEvidenceUploadRow(rowKey, filesToFileList(files));
  };

  const removeEvidenceRow = (rowKey: string, name: string, uploadedAt: number) => {
    // rowKey is either encounter_id (for Visit frequency) or pid
    setRows((prev) => {
      const next = prev.map((r) => {
        // Match by encounter_id first, fallback to pid
        const currentKey = r.encounterId || r.pid;
        return currentKey === rowKey 
          ? { ...r, evidence: (r.evidence || []).filter((e) => !(e.name === name && e.uploadedAt === uploadedAt)) } 
          : r;
      });
      persistEvidenceFrom(next);
      return next;
    });
  };
  // When rows change, aggregate and persist override counts for Gaps summary
  useEffect(() => {
    const agg = rows.reduce(
      (acc, r) => {
        acc.ip += r.ip ? 1 : 0;
        acc.ex += r.ex ? 1 : 0;
        acc.den += r.den ? 1 : 0;
        acc.met += r.met ? 1 : 0;
        acc.exc += r.exc ? 1 : 0;
        acc.not += r.not ? 1 : 0;
        return acc;
      },
      { ip: 0, ex: 0, den: 0, met: 0, exc: 0, not: 0 }
    );
    // Persist overrides using composite key (measureId:submissionMethod)
    const [measureId, submissionMethod = 'MIPS CQM'] = id.split(':');
    const compositeKey = `measureOverrides:${measureId}:${submissionMethod}`;
    try {
      localStorage.setItem(compositeKey, JSON.stringify(agg));
    } catch {
      /* ignore quota errors */
    }
  }, [rows, id]);

  const applyToggle = useCallback((idx: number, field: keyof PatientRow) => {
    setRows((prev) => {
      const next = [...prev];
      const r = { ...next[idx] };
      const originalRow = originalRows[idx];
      
      // Track what's changing for instant confirmation
      const changesKey = `${r.encounterId || r.pid}_${idx}`;

      const isNumerator = field === 'met' || field === 'exc' || field === 'not';

      if (field === 'ex') {
        const newEx = !r.ex;
        r.ex = newEx;
        // Rule 2: denominator eligible if IP and no denominator exclusion
        r.den = r.ip && !newEx;
        if (newEx) {
          // Rule 1: selecting denominator exclusion clears numerators
          r.met = false;
          r.exc = false;
          r.not = false;
        }
      } else if (field === 'den') {
        // If user clicks Denominator while Exclusion is selected, clear exclusion
        if (r.ex) {
          r.ex = false;
          r.den = r.ip && !r.ex;
        }
      } else if (isNumerator) {
        // When exclusion is selected, numerators are locked
        if (r.ex) {
          next[idx] = r;
          return next;
        }
        // only one numerator state can be true
        r.met = field === 'met' ? !r.met : false;
        r.exc = field === 'exc' ? !r.exc : false;
        r.not = field === 'not' ? !r.not : false;
      } else {
        // simple toggle for other fields
        // @ts-expect-error index access is safe here
        r[field] = !r[field];
      }

      // Always keep denominator derived from IP and EX
      r.den = r.ip && !r.ex;

      // Track changes for instant confirmation
      if (originalRow) {
        const changes: any = {};
        let hasChanges = false;
        
        // Check which fields changed compared to original
        const outcomeFields = ['ip', 'ex', 'den', 'met', 'exc', 'not'] as const;
        for (const f of outcomeFields) {
          if (Boolean(r[f]) !== Boolean(originalRow[f])) {
            changes[f] = Boolean(r[f]);
            hasChanges = true;
          }
        }
        
        // Update or remove from changes map
        if (hasChanges) {
          changesMapRef.current.set(changesKey, {
            patientId: r.pid,
            encounterId: r.encounterId,
            stratum: r.stratum || null,  // Normalize empty string to null for consistency
            outcomeChanges: changes,
            rowIndex: idx
          });
        } else {
          // No changes compared to original, remove from map
          changesMapRef.current.delete(changesKey);
        }
      }

      next[idx] = r;
      return next;
    });
  }, [originalRows]);

  // Debounced toggle function to prevent rapid state updates
  const debouncedApplyToggle = useMemo(
    () => debounce((idx: number, field: keyof PatientRow) => {
      applyToggle(idx, field);
    }, 100),
    [applyToggle]
  );

  const toggle = (idx: number, field: keyof PatientRow) => {
    if (isCurrentYearLocked) {
      toast({
        title: `Program year ${programYear} is locked`,
        description: "This year is read-only. Unlock it in Admin → System Settings to make changes.",
        variant: "destructive",
      });
      return;
    }
    const isSensitive = field === 'ex' || field === 'met' || field === 'exc' || field === 'not';
    const acknowledged = localStorage.getItem(ackKey) === '1';
    if (isSensitive && !acknowledged) {
      setPendingToggle({ idx, field });
      setWarnOpen(true);
      return;
    }
    debouncedApplyToggle(idx, field);
  };

  const exportDetail = () => {
    const headers = [
      "TIN",
      "Practice_Name",
      "Practice_Alias",
      "Encounter_ID",
      "Patient_ID",
      "Evaluation_Type",
      "Full_Name",
      "DOB",
      "Gender",
      "NPI",
      "NPI_Name",
      "Date_of_Service",
      "Stratum",
      "CPT_Codes",
      "CPT_Modifiers",
      "ICD10_Codes",
      "Place_of_Service",
      "Initial_Population",
      "Denominator_Exclusion",
      "Denominator",
      "Numerator_Met",
      "Numerator_Exception",
      "Numerator_Not_Met",
      "Data_Source",
      "Notes",
    ];
    const body = rows.map((r) => ({
      // Encounter TIN first (participant TIN on ACO uploads); practice TIN as the MIPS fallback.
      TIN: (r as { tin?: string }).tin || selectedPractice?.tin || "",
      Practice_Name: (r as { tinName?: string }).tinName || selectedPractice?.tin_name || "",
      Practice_Alias: selectedPractice?.practice_alias || "",
      Encounter_ID: r.encounterId || "",
      Patient_ID: r.pid,
      Evaluation_Type: "GROUP", // Currently always GROUP, will be dynamic when NPI view is added
      Full_Name: r.fullName || (r.firstName && r.lastName ? `${r.firstName} ${r.lastName}` : ""),
      DOB: r.dob,
      Gender: r.gender,
      NPI: r.npi,
      NPI_Name: r.npiName,
      Date_of_Service: r.dos,
      Stratum: r.stratum || "N/A",
      CPT_Codes: r.cptCodes,
      CPT_Modifiers: r.cptModifiers,
      ICD10_Codes: r.icd10Codes,
      Place_of_Service: r.placeOfService,
      Initial_Population: r.ip ? "True" : "False",
      Denominator_Exclusion: r.ex ? "True" : "False",
      Denominator: r.den ? "True" : "False",
      Numerator_Met: r.met ? "True" : "False",
      Numerator_Exception: r.exc ? "True" : "False",
      Numerator_Not_Met: r.not ? "True" : "False",
      Data_Source: r.source,
      Notes: r.notes,
    }));
    const csv = toCsv(body, headers);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `measure-${id}-detail.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importDetail = async (file: File) => {
    const text = await file.text();
    const lines = text.trim().split(/\r?\n/);
    const header = lines.shift()?.split(",").map((h) => h.replace(/"/g, "").trim()) || [];
    
    // Debug: Log the header columns
    console.log("CSV Header columns:", header);
    
    const idxOf = (name: string) => header.findIndex((h) => h.toLowerCase() === name.toLowerCase());
    const iEncId = idxOf("Encounter_ID");
    const iPid = idxOf("Patient_ID");
    const iEvalType = idxOf("Evaluation_Type");
    
    // Debug: Log the column indices
    console.log("Column indices - Encounter_ID:", iEncId, "Patient_ID:", iPid, "Evaluation_Type:", iEvalType);
    const iIP = idxOf("Initial_Population");
    const iEx = idxOf("Denominator_Exclusion");
    const iDen = idxOf("Denominator");
    const iMet = idxOf("Numerator_Met");
    const iExc = idxOf("Numerator_Exception");
    const iNot = idxOf("Numerator_Not_Met");
    const iSrc = idxOf("Data_Source");
    const iNotes = idxOf("Notes");

    // Check if Encounter_ID column exists
    if (iEncId < 0) {
      toast({
        title: "Import Failed",
        description: "Encounter_ID column is required. Please ensure your CSV includes this column.",
        variant: "destructive"
      });
      return;
    }

    // Track skipped records
    let skippedCount = 0;
    const skippedPatients: string[] = [];
    
    // Use encounter_id as the key (required, no fallback)
    const parsed = new Map<string, Partial<PatientRow>>();
    let processedCount = 0;
    for (const line of lines) {
      const cols = line.split(/,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)/).map((c) => c.replace(/^\"|\"$/g, ""));
      const encId = cols[iEncId]?.trim();
      const pid = cols[iPid];
      
      if (!pid) continue;
      
      // Encounter_ID is required - skip records without it
      if (!encId) {
        skippedCount++;
        if (skippedPatients.length < 5) { // Track first 5 for display
          skippedPatients.push(pid);
        }
        console.log(`Skipped record - Patient: ${pid}, Encounter_ID: empty/missing`);
        continue;
      }
      
      processedCount++;
      if (processedCount <= 5) {
        console.log(`Processing record ${processedCount} - Patient: ${pid}, Encounter_ID: ${encId}`);
      }
      
      // Use encounter_id as key (no fallback)
      parsed.set(encId, {
        ip: cols[iIP] !== undefined ? toBool(cols[iIP]) : undefined,
        ex: cols[iEx] !== undefined ? toBool(cols[iEx]) : undefined,
        den: cols[iDen] !== undefined ? toBool(cols[iDen]) : undefined,
        met: cols[iMet] !== undefined ? toBool(cols[iMet]) : undefined,
        exc: cols[iExc] !== undefined ? toBool(cols[iExc]) : undefined,
        not: cols[iNot] !== undefined ? toBool(cols[iNot]) : undefined,
        evaluationType: cols[iEvalType] || 'GROUP', // Include evaluation type from CSV
        source: cols[iSrc],
        notes: cols[iNotes],
      });
    }

    // Check if any records were skipped
    if (skippedCount > 0) {
      const examples = skippedPatients.length > 0 
        ? ` Examples: ${skippedPatients.join(", ")}${skippedCount > 5 ? "..." : ""}`
        : "";
      toast({
        title: "Import Warning",
        description: `${skippedCount} records skipped due to missing Encounter_ID.${examples}`,
        variant: "destructive"
      });
    }

    // Check if we have any valid records to import
    if (parsed.size === 0) {
      toast({
        title: "Import Failed",
        description: "No valid records found. Please ensure all records have Encounter_ID values.",
        variant: "destructive"
      });
      return;
    }

    console.log(`Parsed map has ${parsed.size} entries`);
    console.log(`Current rows have ${rows.length} total rows`);
    
    let matchedCount = 0;
    let unmatchedCount = 0;

    setRows((prev) =>
      prev.map((r, idx) => {
        // Match by encounter_id only (strict matching)
        if (!r.encounterId) {
          unmatchedCount++;
          if (unmatchedCount <= 3) {
            console.log(`Row ${idx} has no encounter_id, skipping`);
          }
          return r; // Skip rows without encounter_id
        }
        const key = r.encounterId.trim();
        const p = parsed.get(key);
        if (!p) {
          unmatchedCount++;
          if (unmatchedCount <= 3) {
            console.log(`Row ${idx} - No match found for encounter_id: ${key}`);
          }
          return r;
        }
        matchedCount++;
        if (matchedCount <= 3) {
          console.log(`Row ${idx} - Matched encounter_id: ${key}`);
        }
        const updated: PatientRow = {
          ...r,
          ip: p.ip ?? r.ip,
          ex: p.ex ?? r.ex,
          // Denominator derived by rule 2
          den: r.den,
          // enforce mutual exclusivity for numerator states
          met: p.met ?? r.met,
          exc: p.exc ?? r.exc,
          not: p.not ?? r.not,
          evaluationType: p.evaluationType ?? r.evaluationType ?? 'GROUP', // Include evaluation type
          source: p.source ?? r.source,
          notes: p.notes ?? r.notes,
        };
        // Recalculate denominator based on IP and EX
        updated.den = updated.ip && !updated.ex;

        // Enforce numerator mutual exclusivity if any provided in CSV
        if (p.met || p.exc || p.not) {
          updated.met = !!p.met;
          updated.exc = !!p.exc && !updated.met;
          updated.not = !!p.not && !updated.met && !updated.exc;
        }

        // If Denominator Exclusion is true, clear numerators per rule 1
        if (updated.ex) {
          updated.met = false;
          updated.exc = false;
          updated.not = false;
        }
        return updated;
      })
    );

    // Mark that we've imported data
    setHasImportedCSV(true);
    
    // Log final summary
    console.log(`Import Summary: Matched ${matchedCount} records, Unmatched ${unmatchedCount} records`);
    
    // Show success message with count of imported records
    const successMessage = skippedCount > 0
      ? `${parsed.size} records imported from CSV (${skippedCount} skipped, ${matchedCount} matched). Click 'Confirm & Recalculate' to save changes.`
      : `${parsed.size} records imported from CSV (${matchedCount} matched). Click 'Confirm & Recalculate' to save changes.`;
    
    toast({ 
      title: "Import Complete", 
      description: successMessage,
      variant: "default"
    });
  };

  const confirm = async () => {
    if (!selectedPracticeId) {
      toast({
        title: "Error",
        description: "No practice selected. Please select a practice first.",
        variant: "destructive",
      });
      return;
    }

    // Prevent double-clicks
    if (isProcessing) return;

    // Set processing state immediately
    setIsProcessing(true);
    setConfirmButtonText("Processing...");

    try {
      const [measureId, submissionMethod = 'MIPS CQM'] = id.split(':');
      
      // Check if we have imported CSV data
      let batchUpdates;
      
      if (hasImportedCSV) {
        // Only send rows that actually changed compared to original
        batchUpdates = [];
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const original = originalRows[i];
          
          // Check if any outcome field changed
          if (original && (
            row.ip !== original.ip ||
            row.ex !== original.ex ||
            row.den !== original.den ||
            row.met !== original.met ||
            row.exc !== original.exc ||
            row.not !== original.not
          )) {
            batchUpdates.push({
              patientId: row.pid,
              measureId,
              submissionMethod,
              evaluationType: row.evaluationType || 'GROUP', // Use from CSV if present, default to GROUP
              outcomeChanges: {
                ip: row.ip,
                ex: row.ex,
                den: row.den,
                met: row.met,
                exc: row.exc,
                not: row.not
              },
              encounterId: row.encounterId,
              stratum: row.stratum  // Add stratum support
            });
          }
        }
        
        console.log(`DEBUG: CSV import - ${batchUpdates.length} rows changed out of ${rows.length} total`);
      } else {
        // Use the pre-tracked changes for instant confirmation (from bubble clicks)
        batchUpdates = Array.from(changesMapRef.current.values()).map(change => ({
          patientId: change.patientId,
          measureId,
          submissionMethod,
          evaluationType: 'GROUP', // Default to GROUP, will be dynamic when NPI view is added
          outcomeChanges: change.outcomeChanges,
          encounterId: change.encounterId,
          stratum: change.stratum  // Use the stratum that was stored when the change was made
        }));
      }
      // Process batch updates if there are any changes
      if (batchUpdates.length > 0) {
        try {
          // Set flag for Summary page to know there are pending updates
          sessionStorage.setItem('detailUpdatePending', 'true');
          
          // Save pending state to sessionStorage - only save first page (100 rows) to avoid quota issues
          const pendingStateKey = `pendingState_${measureId}`;
          const rowsToStore = rows.slice(0, rowsPerPage); // Only store first 100 rows
          sessionStorage.setItem(pendingStateKey, JSON.stringify({
            rows: rowsToStore, // Only first page to avoid quota exceeded
            totalRows: rows.length, // Store total count for reference
            timestamp: Date.now(),
            updateCount: batchUpdates.length
          }));
          
          console.log('DEBUG: Sending batch update with', batchUpdates.length, 'updates');
          const result = await batchUpdatePatientOutcomes(selectedPracticeId, batchUpdates, programYear);
          
          // Check if this is a job response (non-blocking)
          if (result.jobId) {
            // Job submitted - start polling
            setCurrentJobId(result.jobId);
            setConfirmButtonText("Processing...");
            
            toast({
              title: "Updates Submitted",
              description: `Processing ${batchUpdates.length} updates in background. You can navigate freely.`,
            });
            
            // Start polling for job status
            pollJobStatus(result.jobId);
            
            console.log('DEBUG: Job submitted with ID:', result.jobId);
            
          } else if (result.results) {
            // Old synchronous response (backward compatibility)
            // Check for conflict resolution feedback in the response
            if (result.results?.conflictResolutions?.length > 0) {
              handleOutcomeUpdateResponse({
                hasConflictResolution: true,
                conflictResolution: result.results.conflictResolutions
              });
            }
            
            const successCount = result.results?.success?.length || 0;
            const errorCount = result.results?.errors?.length || 0;
            
            if (successCount > 0) {
              // Show success feedback
              setConfirmButtonText("✓ Updated");
        toast({
          title: "Success",
                description: `Updated outcomes for ${successCount} encounter(s).`,
              });
              
              // Clear pending state
              sessionStorage.removeItem(pendingStateKey);
              
              // Reset button after 2 seconds
              setTimeout(() => {
                setConfirmButtonText("Confirm & Recalculate");
              }, 2000);
              
        // Refresh data after successful updates
        const evalContext4 = { evaluationType, npi: evaluationType === 'NPI' ? selectedNpi : undefined, year: programYear };
        const data = await getPatientRows(measureId, submissionMethod, evalContext4);
        setRows(data as PatientRow[]);
        setOriginalRows(data as PatientRow[]); // Update original data after save
              changesMapRef.current.clear(); // Clear tracked changes after successful save
              setHasImportedCSV(false); // Reset import flag
              console.log('DEBUG: Data refreshed after batch updates');
            }
            
            if (errorCount > 0) {
              // Show error feedback if no successes
              if (successCount === 0) {
                setConfirmButtonText("Failed - Try Again");
                setTimeout(() => {
                  setConfirmButtonText("Confirm & Recalculate");
                }, 2000);
              }
              
              toast({
                title: "Warning",
                description: `Failed to update ${errorCount} encounter(s). See console for details.`,
                variant: "destructive",
              });
              console.error('Batch update errors:', result.results.errors);
            }
          }
        } catch (error) {
          console.error('DEBUG: Error in batch update:', error);
          // Show error feedback
          setConfirmButtonText("Failed - Try Again");
        toast({
          title: "Error",
            description: "Failed to update patient outcomes. Please try again.",
          variant: "destructive",
        });
          
          // Reset button after 2 seconds
          setTimeout(() => {
            setConfirmButtonText("Confirm & Recalculate");
          }, 2000);
        }
      } else {
        // No changes detected
        setConfirmButtonText("No Changes");
        toast({
          title: "No Changes",
          description: "No patient outcome changes were detected.",
        });
        console.log('DEBUG: No changes detected for any patients');
        
        // Reset button after 2 seconds
        setTimeout(() => {
          setConfirmButtonText("Confirm & Recalculate");
        }, 2000);
      }
    } catch (error) {
      console.error('DEBUG: Unexpected error in confirm():', error);
      setIsProcessing(false);
      setConfirmButtonText("Failed - Try Again");
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const importInputRef = useRef<HTMLInputElement>(null);
  const onImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    await importDetail(file);
    e.currentTarget.value = '';
  };
  const triggerImport = () => {
    importInputRef.current?.click();
  };

  // Column settings for patient detail table
  type ColKey =
    | 'pid' | 'fullName' | 'first' | 'last' | 'dob' | 'gender' | 'npiName' | 'dos'
    | 'stratum' | 'ip' | 'ex' | 'den' | 'met' | 'exc' | 'not'
    | 'source' | 'notes'
    | 'npi' | 'cptCodes' | 'cptModifiers' | 'icd10Codes' | 'placeOfService';

  const allColumnDefs: ColumnDef[] = [
    { key: 'pid', label: 'Patient ID', tooltip: 'Unique patient identifier', lock: true },
    { key: 'fullName', label: 'Full Name', tooltip: 'Patient full name' },
    { key: 'first', label: 'First', tooltip: 'First name' },
    { key: 'last', label: 'Last', tooltip: 'Last name' },
    { key: 'dob', label: 'DOB', tooltip: 'Date of birth' },
    { key: 'gender', label: 'Gender' },
    { key: 'npiName', label: 'NPI Name', tooltip: 'Rendering provider name' },
    { key: 'dos', label: 'Date of Service', tooltip: 'Encounter date' },

    { key: 'stratum', label: 'Stratum', tooltip: 'Performance stratum for multi-strata measures' },
    { key: 'ip', label: 'Initial Pop.', tooltip: 'Initial population eligibility', lock: true },
    { key: 'ex', label: 'Denominator Exclusion', tooltip: 'Denominator exclusion', lock: true },
    { key: 'den', label: 'Denominator', tooltip: 'Denominator eligibility', lock: true },
    { key: 'met', label: 'Numerator Met', tooltip: 'Meets numerator', lock: true },
    { key: 'exc', label: 'Num Exception', tooltip: 'Numerator exception', lock: true },
    { key: 'not', label: 'Numerator Not Met', tooltip: 'Does not meet numerator', lock: true },

    { key: 'source', label: 'Data Source', tooltip: 'EHR, Claims, Registry', },
    { key: 'notes', label: 'Notes / Evidence', tooltip: 'Notes and file uploads', lock: true },

    // Optional fields user may add
    { key: 'npi', label: 'NPI #', tooltip: 'National Provider Identifier' },
    { key: 'cptCodes', label: 'All CPT Codes', tooltip: 'All CPT/HCPCS codes' },
    { key: 'cptModifiers', label: 'CPT Modifiers', tooltip: 'CPT modifiers' },
    { key: 'icd10Codes', label: 'All ICD-10 Codes', tooltip: 'Diagnosis codes' },
    { key: 'placeOfService', label: 'Place of Service', tooltip: 'POS code' },
  ];

  const defaultOrder: ColKey[] = [
    'pid','fullName','dob','gender','npiName','dos',
    'stratum','ip','ex','den','met','exc','not',
    'source','notes'
  ];
  const defaultVisibility: Record<ColKey, boolean> = {
    pid: true, fullName: true, first: false, last: false, dob: true, gender: true, npiName: true, dos: true,
    stratum: true, ip: true, ex: true, den: true, met: true, exc: true, not: true,
    source: true, notes: true,
    npi: false, cptCodes: false, cptModifiers: false, icd10Codes: false, placeOfService: false,
  };
  const defaultColSettings: ColumnSettingsValue = { order: defaultOrder, visibility: defaultVisibility, tooltips: false };

// Column view presets
interface ColumnPreset { id: string; name: string; settings: ColumnSettingsValue; builtin?: boolean }
const outcomesOrder: ColKey[] = ['pid','fullName','dos','stratum','ip','ex','den','met','exc','not','notes'];
const outcomesVisibility: Record<ColKey, boolean> = {
  pid: true, fullName: true, first: false, last: false, dob: false, gender: false, npiName: false, dos: true,
  stratum: true, ip: true, ex: true, den: true, met: true, exc: true, not: true,
  source: false, notes: true,
  npi: false, cptCodes: false, cptModifiers: false, icd10Codes: false, placeOfService: false,
};
const outcomesSettings: ColumnSettingsValue = { order: outcomesOrder, visibility: outcomesVisibility, tooltips: false };
const builtInPresets: ColumnPreset[] = [
  { id: 'default', name: 'Default', settings: outcomesSettings, builtin: true },
  { id: 'provider', name: 'Provider Details', settings: defaultColSettings, builtin: true },
];
const [presets, setPresets] = useState<ColumnPreset[]>(() => {
  try {
    const stored = JSON.parse(userStorage.getItem('measureDetail:colPresets') || 'null');
    const userPresets = (Array.isArray(stored) ? stored : []).filter(p => !p.builtin);
    return [...builtInPresets, ...userPresets];
  } catch { return builtInPresets }
});
const [activePresetId, setActivePresetId] = useState<string>(() => {
  try { return userStorage.getItem('measureDetail:activePresetId') || 'default' } catch { return 'default' }
});
const [colModalOpen, setColModalOpen] = useState(false);
const [manageOpen, setManageOpen] = useState(false);
const [colSettings, setColSettings] = useState<ColumnSettingsValue>(() => {
  try {
    const saved = JSON.parse(userStorage.getItem('measureDetail:colSettings') || 'null');
    
    if (saved) {
      // Check if this is old settings (no fullName property in visibility)
      if (!saved.visibility.hasOwnProperty('fullName')) {
        console.log('[Detail] Migrating old column settings to include Full Name column');
        
        // Add fullName to visibility settings
        saved.visibility.fullName = true;
        saved.visibility.first = false;
        saved.visibility.last = false;
        
        // Update the order array to replace first/last with fullName
        if (saved.order && Array.isArray(saved.order)) {
          const firstIndex = saved.order.indexOf('first');
          const lastIndex = saved.order.indexOf('last');
          
          if (firstIndex !== -1) {
            // Replace 'first' with 'fullName'
            saved.order[firstIndex] = 'fullName';
            
            // Remove 'last' if it exists (we don't want both)
            if (lastIndex !== -1 && lastIndex !== firstIndex) {
              saved.order.splice(lastIndex, 1);
            }
          } else if (lastIndex !== -1) {
            // If only 'last' exists, replace it with 'fullName'
            saved.order[lastIndex] = 'fullName';
          } else if (!saved.order.includes('fullName')) {
            // If neither first nor last exist, add fullName after pid
            const pidIndex = saved.order.indexOf('pid');
            if (pidIndex !== -1) {
              saved.order.splice(pidIndex + 1, 0, 'fullName');
            } else {
              // As last resort, add at beginning
              saved.order.unshift('fullName');
            }
          }
        }
        
        // Save the migrated settings back to localStorage
        userStorage.setItem('measureDetail:colSettings', JSON.stringify(saved));
        console.log('[Detail] Migration complete - Full Name column is now visible');
      }
      
      return saved;
    }
    
    // No saved settings, use defaults
    return outcomesSettings;
  } catch (error) {
    console.error('[Detail] Error loading column settings:', error);
    return outcomesSettings;
  }
});
useEffect(() => {
  userStorage.setItem('measureDetail:colSettings', JSON.stringify(colSettings));
}, [colSettings]);
useEffect(() => {
  // Only save user presets, never system presets
  const userPresets = presets.filter(p => !p.builtin);
  userStorage.setItem('measureDetail:colPresets', JSON.stringify(userPresets));
}, [presets]);
useEffect(() => {
  userStorage.setItem('measureDetail:activePresetId', activePresetId);
}, [activePresetId]);

// Migrate legacy preset names/ids (Outcomes only -> Default, old Default -> Provider Details)
useEffect(() => {
  const hasLegacyOutcomes = presets.some(p => p.id === 'outcomes');
  const hasProvider = presets.some(p => p.id === 'provider');
  if (hasLegacyOutcomes || !hasProvider) {
    const legacyDefault = presets.find(p => p.id === 'default');
    const legacyOutcomes = presets.find(p => p.id === 'outcomes');
    const others = presets.filter(p => !['default','outcomes','provider'].includes(p.id));

    const providerPreset = legacyDefault
      ? { id: 'provider', name: 'Provider Details', settings: legacyDefault.settings, builtin: true as const }
      : { id: 'provider', name: 'Provider Details', settings: defaultColSettings, builtin: true as const };

    const newDefault = legacyOutcomes
      ? { id: 'default', name: 'Default', settings: legacyOutcomes.settings, builtin: true as const }
      : { id: 'default', name: 'Default', settings: outcomesSettings, builtin: true as const };

    const next = [newDefault, providerPreset, ...others];
    setPresets(next);
    if (activePresetId === 'outcomes') {
      setActivePresetId('default');
      setColSettings(newDefault.settings);
    }
  }
  // run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const applyPreset = (id: string) => {
  const p = presets.find(x => x.id === id);
  if (!p) return;
  setColSettings(p.settings);
  setActivePresetId(id);
};
const saveCurrentAsPreset = () => {
  const name = window.prompt('Preset name');
  if (!name) return;
  const id = `user-${Date.now()}`;
  const p: ColumnPreset = { id, name, settings: colSettings };
  setPresets(prev => [...prev, p]);
  setActivePresetId(id);
};
const deleteCurrentPreset = () => {
  const cur = presets.find(p => p.id === activePresetId);
  if (!cur || cur.builtin) return;
  if (!window.confirm(`Delete preset "${cur.name}"?`)) return;
  setActivePresetId('default');
  setColSettings(outcomesSettings);
};

  const visibleOrder = useMemo(() => {
    const merged = Array.from(new Set([...(colSettings.order || []), ...allColumnDefs.map(d => d.key as ColKey)]));
    return merged.filter(k => (colSettings.visibility as any)[k as string]) as ColKey[];
  }, [colSettings, allColumnDefs]);

  type SortKey = 'pid' | 'last' | 'first' | 'dob' | 'dos' | 'npiName' | 'stratum' | 'source' | 'ip' | 'ex' | 'den' | 'met' | 'exc' | 'not';
  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'last', label: 'Last name' },
    { key: 'first', label: 'First name' },
    { key: 'pid', label: 'Patient ID' },
    { key: 'dob', label: 'DOB' },
    { key: 'dos', label: 'Date of Service' },
    { key: 'npiName', label: 'NPI Name' },
    { key: 'stratum', label: 'Stratum' },
    { key: 'source', label: 'Data Source' },
    { key: 'ip', label: 'Initial Pop.' },
    { key: 'ex', label: 'Denominator Excl.' },
    { key: 'den', label: 'Denominator' },
    { key: 'met', label: 'Numerator Met' },
    { key: 'exc', label: 'Numerator Exception' },
    { key: 'not', label: 'Numerator Not Met' },
  ];
  const [sorters, setSorters] = useState<{ key: SortKey; dir: 'asc' | 'desc' }[]>([{ key: 'last', dir: 'asc' }]);
  const [sortOpen, setSortOpen] = useState(false);

  // Filters for detailed results
  const [filters, setFilters] = useState({ ex: false, exc: false, met: false, not: false });
  const activeFilterCount = useMemo(() => Number(filters.ex) + Number(filters.exc) + Number(filters.met) + Number(filters.not), [filters]);
  const filteredRows = useMemo(() => {
    const anyActive = filters.ex || filters.exc || filters.met || filters.not;
    if (!anyActive) return rows;
    return rows.filter((r) =>
      (filters.ex && r.ex) ||
      (filters.exc && r.exc) ||
      (filters.met && r.met) ||
      (filters.not && r.not)
    );
  }, [rows, filters]);

  // PERFORMANCE: Memoize unique strata calculation
  const allStrata = useMemo(() => {
    return [...new Set(rows.map(row => row.stratum).filter(Boolean))].sort();
  }, [rows]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    if (!sorters.length) return arr;
    const getVal = (r: PatientRow, key: SortKey) => {
      switch (key) {
        case 'pid': return Number(r.pid);
        case 'last': return r.lastName.toLowerCase();
        case 'first': return r.firstName.toLowerCase();
        case 'dob': return new Date(r.dob).getTime() || 0;
        case 'dos': return new Date(r.dos).getTime() || 0;
        case 'npiName': return r.npiName.toLowerCase();
        case 'stratum': return r.stratum?.toLowerCase() || 'zzz'; // Sort N/A values to end
        case 'source': return r.source.toLowerCase();
        case 'ip': return Number(r.ip);
        case 'ex': return Number(r.ex);
        case 'den': return Number(r.den);
        case 'met': return Number(r.met);
        case 'exc': return Number(r.exc);
        case 'not': return Number(r.not);
      }
    };
    arr.sort((a, b) => {
      for (const s of sorters) {
        const av = getVal(a, s.key);
        const bv = getVal(b, s.key);
        if (av < bv) return s.dir === 'asc' ? -1 : 1;
        if (av > bv) return s.dir === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return arr;
  }, [filteredRows, sorters]);
  
  // Calculate paginated rows
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return sortedRows.slice(startIndex, endIndex);
  }, [sortedRows, currentPage, rowsPerPage]);
  
  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sorters]);

  // Detail side sheet state
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const openDetail = (idx: number) => { 
    setActiveIdx(idx); 
    setDetailOpen(true); 
    setShowAllActivityLog(false); // Reset to show only 5 when opening a new patient
    
    // Fetch activity log for this patient
    if (rows[idx]) {
      const [measureId] = id.split(':');
      fetchActivityLog(rows[idx].pid, measureId, rows[idx].encounterId);
    }
  };
  const updateActive = (mutator: (r: PatientRow) => PatientRow) => {
    if (isCurrentYearLocked) {
      toast({
        title: `Program year ${programYear} is locked`,
        description: "This year is read-only. Unlock it in Admin → System Settings to make changes.",
        variant: "destructive",
      });
      return;
    }
    setRows((prev) => {
      if (activeIdx === null) return prev;
      const next = [...prev];
      let r = { ...next[activeIdx] };
      r = mutator(r);
      // enforce rules
      r.den = r.ip && !r.ex;
      if (r.ex) { r.met = false; r.exc = false; r.not = false; }
      next[activeIdx] = r;
      return next;
    });
  };

  return (
    <div>
      <Helmet>
        <title>Measure: {id.replace(':', ' | ')} Patient Drilldown</title>
        <meta name="description" content={`Per-patient drilldown for ${id}: edit criteria and sync with Quality Summary.`} />
        <link rel="canonical" href={`/programs/mips/gap-management/quality/measure/${id}`} />
      </Helmet>

      {!embedded && <YearLockBanner />}

      <header className={`mb-6 flex items-center justify-between ${embedded ? 'mb-3' : ''}`}>
        <div>
          <h1 className={embedded ? 'text-xl font-semibold' : 'text-3xl font-bold'}>
            Measure: {id.replace(':', ' | ')}
          </h1>
          {!embedded && (
            <p className="text-sm text-muted-foreground">
              Review eligible patients and update criteria status.
            </p>
          )}
        </div>
        {!embedded && (
        <div className="flex items-center gap-3">
          {/* Evaluation Type Toggle - fixed width container */}
          <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-muted/30 min-w-[480px]">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex items-center gap-1">
              <Button
                variant={evaluationType === 'GROUP' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-3"
                onClick={() => setEvaluationType('GROUP')}
              >
                Group/TIN
              </Button>
              <Button
                variant={evaluationType === 'NPI' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-3"
                onClick={() => setEvaluationType('NPI')}
              >
                NPI
              </Button>
            </div>
            
            {/* Show practice name when GROUP mode, NPI dropdown when NPI mode */}
            {evaluationType === 'GROUP' ? (
              <div className="w-[280px] h-8 px-3 flex items-center text-sm font-medium text-foreground truncate border rounded-md bg-background">
                {selectedPractice?.tin_name || selectedPractice?.practice_alias || 'All Providers'}
              </div>
            ) : (
              <Select
                value={selectedNpi}
                onValueChange={(value) => setSelectedNpi(value)}
                disabled={loadingProviders || providers.length === 0}
              >
                <SelectTrigger className="w-[280px] h-8">
                  <SelectValue placeholder={loadingProviders ? "Loading providers..." : "Select provider"} />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((provider) => (
                    <SelectItem key={provider.npi} value={provider.npi}>
                      {provider.displayName}
                    </SelectItem>
                  ))}
                  {providers.length === 0 && !loadingProviders && (
                    <SelectItem value="__no_providers__" disabled>
                      No providers found
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
          <Button asChild variant="outline"><Link to={backTo}>{backLabel}</Link></Button>
        </div>
        )}
      </header>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-2xl font-bold">Detailed Results</CardTitle>
            {!isManualEntry && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Tooltips</span>
                  <Switch checked={colSettings.tooltips} onCheckedChange={(v)=>setColSettings(prev=>({ ...prev, tooltips: !!v }))} />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Columns className="mr-2 h-4 w-4" />
                      View
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="z-50 bg-background" align="end">
                    <DropdownMenuLabel>Column Views</DropdownMenuLabel>
                    <DropdownMenuRadioGroup 
                      value={activePresetId}
                      onValueChange={(v) => {
                        if (presets.some(p=>p.id===v)) { 
                          applyPreset(v); 
                        }
                      }}
                    >
                      {/* System views */}
                      {presets.filter(p=>p.builtin).map((p) => (
                        <DropdownMenuRadioItem key={p.id} value={p.id}>
                          {p.name}
                        </DropdownMenuRadioItem>
                      ))}
                      {/* User views */}
                      {presets.some(p=>!p.builtin) && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs">My Views</DropdownMenuLabel>
                          {presets.filter(p=>!p.builtin).map((p) => (
                            <DropdownMenuRadioItem key={p.id} value={p.id}>
                              {p.name}
                            </DropdownMenuRadioItem>
                          ))}
                        </>
                      )}
                    </DropdownMenuRadioGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setManageOpen(true)}>
                      <Settings2 className="mr-2 h-4 w-4" />
                      Manage Views…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Button variant="outline" size="sm" onClick={() => setSortOpen(o => !o)}>
                <ArrowUpDown className="mr-2 h-4 w-4" />
                Sort & Filter ({sorters.length + activeFilterCount})
              </Button>

            </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isManualEntry ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Manual Entry Mode</h3>
              <p className="text-muted-foreground max-w-md">
                This measure is currently in manual entry mode. Patient-level data is not available when using manual data entry.
              </p>
              <p className="text-muted-foreground max-w-md mt-2">
                To view patient details, switch this measure back to "Recalculate from data" mode in the Gap Management page.
              </p>
              <Button asChild variant="outline" className="mt-4">
                <Link to={backTo}>{backLabel}</Link>
              </Button>
            </div>
          ) : (
            <>
          {/* Pagination Controls */}
          <div className="mb-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                {isLoadingMore ? (
                  <span className="flex items-center gap-2">
                    <RefreshCcw className="h-3 w-3 animate-spin" />
                    Loading patients... ({loadedRowsCount} loaded)
                  </span>
                ) : (
                  `Page ${currentPage} of ${totalPages} (${sortedRows.length} total)`
                )}
              </span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
              </Button>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-2 py-1">
              <span className="text-sm font-medium">Perform Bulk Updates</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:-translate-y-0.5 transition-transform" aria-label="Bulk updates info">
                      <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-xs z-50">
                    <div className="text-sm space-y-2">
                      <p>Use Export to get the full gap list as a CSV; make the necessary changes and Import to apply bulk changes.</p>
                      <p>"Confirm & Recalculate" to update results throughout the registry.</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button variant="outline" size="sm" onClick={exportDetail}>
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
              <input ref={importInputRef} type="file" accept=".csv,text/csv" className="sr-only" onChange={onImportChange} />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={triggerImport}
                disabled={isLoadingMore || isCurrentYearLocked}
                title={isCurrentYearLocked ? `Program year ${programYear} is locked (read-only)` : (isLoadingMore ? "Please wait for all data to load before importing" : "Import CSV file")}
              >
                <Upload className="mr-2 h-4 w-4" />
                {isLoadingMore ? 'Loading...' : 'Import CSV'}
              </Button>
              <Button 
                size="sm" 
                onClick={confirm}
                disabled={isProcessing || currentJobId !== null || isCurrentYearLocked}
                title={isCurrentYearLocked ? `Program year ${programYear} is locked (read-only)` : undefined}
                className={cn(
                  "transition-all duration-200",
                  (isProcessing || currentJobId || isCurrentYearLocked) && "opacity-50 cursor-not-allowed"
                )}
              >
                <RefreshCcw className={cn("mr-2 h-4 w-4", (isProcessing || currentJobId) && "animate-spin")} />
                {confirmButtonText}
              </Button>
            </div>
          </div>
          {sortOpen && (
            <div className="mb-4 rounded-lg border bg-muted/30 p-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                {sorters.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-1 rounded-md border px-2 py-1 bg-background">
                    <Select
                      value={s.key as any}
                      onValueChange={(v: any) => setSorters(prev => prev.map((p, i) => i === idx ? { ...p, key: v } : p))}
                    >
                      <SelectTrigger className="h-8 w-[170px]"><SelectValue placeholder="Field" /></SelectTrigger>
                      <SelectContent className="z-50 bg-background">
                        {sortOptions.map(o => (<SelectItem key={o.key} value={o.key as any}>{o.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSorters(prev => prev.map((p, i) => i === idx ? { ...p, dir: p.dir === 'asc' ? 'desc' : 'asc' } : p))} aria-label="Toggle sort direction">
                      <ArrowUpDown className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSorters(prev => prev.filter((_, i) => i !== idx))} aria-label="Remove sort">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setSorters(prev => [...prev, { key: 'last', dir: 'asc' }])}>+ Add sort</Button>
              </div>
              <Separator />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="font-medium">Filters</div>
                <Button variant="ghost" size="sm" onClick={() => setFilters({ ex: false, exc: false, met: false, not: false })}>Clear filters</Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant={filters.ex ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilters(prev => ({ ...prev, ex: !prev.ex }))}
                >
                  Exclusions
                </Button>
                <Button
                  variant={filters.exc ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilters(prev => ({ ...prev, exc: !prev.exc }))}
                >
                  Exception
                </Button>
                <Button
                  variant={filters.met ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilters(prev => ({ ...prev, met: !prev.met }))}
                >
                  Numerator Met
                </Button>
                <Button
                  variant={filters.not ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilters(prev => ({ ...prev, not: !prev.not }))}
                >
                  Numerator Not Met
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  {visibleOrder.map((key) => {
                    const def = allColumnDefs.find((d) => d.key === key)!;
                    const label = colSettings.tooltips && def.tooltip ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help">{def.label}</span>
                          </TooltipTrigger>
                          <TooltipContent className="z-50">{def.tooltip}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      def.label
                    );
                    const headerClassName = cn(
                      (key === 'first' || key === 'last' || key === 'fullName') ? "text-left max-w-32 whitespace-normal leading-tight" : "text-center"
                    );
                    return <TableHead key={key} className={headerClassName}>{label}</TableHead>;
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // PERFORMANCE: Pre-compute index map to avoid O(n²) complexity
                  // CRITICAL: For strata measures, include stratum in the key to ensure uniqueness
                  // For non-strata measures, use encounter_id alone as before
                  const indexMap = new Map(rows.map((row, idx) => {
                    // Create unique key including stratum for strata measures
                    const key = row.stratum 
                      ? `${row.encounterId || row.pid}_${row.stratum}`
                      : (row.encounterId || row.pid);
                    return [key, idx];
                  }));
                  
                  return paginatedRows.map((r, i) => {
                    // Use the same key logic for lookup
                    const key = r.stratum 
                      ? `${r.encounterId || r.pid}_${r.stratum}`
                      : (r.encounterId || r.pid);
                    const originalIdx = indexMap.get(key)!;
                  return (
                      <PatientTableRow
                        key={key}  // Use unique key (includes stratum for strata measures) for proper React reconciliation
                        row={r}
                        index={i}
                        originalIdx={originalIdx}
                        visibleOrder={visibleOrder}
                        colSettings={colSettings}
                              allStrata={allStrata} 
                        onToggle={toggle}
                        onOpenDetail={openDetail}
                        locked={isCurrentYearLocked}
                      />
                  );
                  });
                })()}
              </TableBody>
            </Table>
          </div>
          </>
          )}
        </CardContent>
      </Card>

      <ColumnSettingsModal
        open={colModalOpen}
        onOpenChange={setColModalOpen}
        columns={allColumnDefs}
        value={colSettings}
        defaultValue={defaultColSettings}
        onApply={(v) => { setColSettings(v); setColModalOpen(false); }}
      />

      <ManageViewsModal
        open={manageOpen}
        onOpenChange={setManageOpen}
        presets={presets as any}
        activePresetId={activePresetId as any}
        allColumns={allColumnDefs as any}
        systemDefault={defaultColSettings as any}
        onCommit={(nextPresets, activeId) => {
          setPresets(nextPresets as any);
          if (activeId) {
            setActivePresetId(activeId as any);
            const p = (nextPresets as any).find((x: any) => x.id === activeId);
            if (p) setColSettings(p.settings as any);
          }
        }}
      />

      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="bg-background sm:max-w-[48rem] w-[95vw] overflow-y-auto">
          <div className="relative px-6 pt-2 pb-6">
          <SheetHeader>
              <div className="space-y-3">
                {/* Patient-centric Header */}
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <SheetTitle className="text-2xl font-bold text-slate-900 leading-tight mb-2">
                      {getPatientDisplayName(activeIdx !== null ? rows[activeIdx] : undefined)}
                    </SheetTitle>
                    <div className="text-sm text-slate-500 flex items-center gap-6">
                      <span className="flex items-center gap-2">
                        <span className="text-slate-400">DOB:</span>
                        <span className="font-medium text-slate-600">{activeIdx !== null ? rows[activeIdx].dob : ''}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-slate-400">Gender:</span>
                        <span className="font-medium text-slate-600">{activeIdx !== null ? rows[activeIdx].gender : ''}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-slate-400">Patient ID:</span>
                        <span className="font-medium text-slate-600">{activeIdx !== null ? rows[activeIdx].pid : ''}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="text-slate-400">Source:</span>
                        <span className="font-medium text-slate-600">{activeIdx !== null ? rows[activeIdx].source : ''}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
          </SheetHeader>

          {activeIdx !== null && (
              <div className="mt-6">
                {/* Visit Information - Single row */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-3">VISIT INFORMATION</h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="grid grid-cols-4 gap-6">
                      <div>
                        <span className="text-sm text-slate-600">Date of Service</span>
                        <div className="mt-1 text-base font-medium">{rows[activeIdx].dos}</div>
                </div>
                      <div>
                        <span className="text-sm text-slate-600">Place of Service</span>
                        <div className="mt-1 text-base font-medium">{rows[activeIdx].placeOfService}</div>
                </div>
                      <div>
                        <span className="text-sm text-slate-600">Provider NPI</span>
                        <div className="mt-1 text-base font-medium">{rows[activeIdx].npi || '—'}</div>
                </div>
                      <div>
                        <span className="text-sm text-slate-600">Provider Name</span>
                        <div className="mt-1 text-base font-medium">{rows[activeIdx].npiName || '—'}</div>
                </div>
                </div>
                </div>
                </div>

                <Separator className="my-6" />

                {/* Visit Data - Single streamlined card */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-3">VISIT DATA</h3>
                  <div className="border border-slate-200 rounded-lg p-4 bg-white">
                    <div className="grid grid-cols-3 gap-6">
                <div>
                        <span className="text-sm text-slate-600 font-medium">CPT Codes</span>
                        <div className="mt-1 text-base font-mono">
                          {rows[activeIdx].cptCodes ? 
                            rows[activeIdx].cptCodes.split(',').map(code => code.trim()).join(', ') : 
                            '—'
                          }
                </div>
                </div>
                <div>
                        <span className="text-sm text-slate-600 font-medium">Modifiers</span>
                        <div className="mt-1 text-base font-mono">
                          {rows[activeIdx].cptModifiers ? 
                            rows[activeIdx].cptModifiers.split(',').map(mod => mod.trim()).join(', ') : 
                            '—'
                          }
                        </div>
                </div>
                <div>
                        <span className="text-sm text-slate-600 font-medium">ICD-10 Codes</span>
                        <div className="mt-1 text-base font-mono">
                          {rows[activeIdx].icd10Codes ? 
                            rows[activeIdx].icd10Codes.split(',').map(code => code.trim()).join(', ') : 
                            '—'
                          }
                </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator className="my-6" />

                {/* Measure Outcome Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-3">MEASURE OUTCOME</h3>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-5">
                    <div className="mb-4 pl-4">
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold text-slate-800">
                        Measure: {id.replace(':', ' | ')}
                      </h4>
                      <p className="text-xs text-slate-600">
                        Review and edit patient outcomes for this measure
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 italic mb-2">Review and edit outcomes</p>
                  
                  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                    <div className="grid grid-cols-6 divide-x divide-slate-200">
                      <div className="text-xs font-semibold text-slate-600 text-center py-3 px-2 bg-slate-50 border-b border-slate-200">
                        INITIAL POP.
                      </div>
                      <div className="text-xs font-semibold text-slate-600 text-center py-3 px-2 bg-slate-50 border-b border-slate-200">
                        EXCLUSIONS
                      </div>
                      <div className="text-xs font-semibold text-slate-600 text-center py-3 px-2 bg-slate-50 border-b border-slate-200">
                        DENOMINATOR
                      </div>
                      <div className="text-xs font-semibold text-slate-600 text-center py-3 px-2 bg-slate-50 border-b border-slate-200">
                        MET
                      </div>
                      <div className="text-xs font-semibold text-slate-600 text-center py-3 px-2 bg-slate-50 border-b border-slate-200">
                        EXCEPTION
                      </div>
                      <div className="text-xs font-semibold text-slate-600 text-center py-3 px-2 bg-slate-50 border-b border-slate-200">
                        NOT MET
                      </div>
                      <div className="p-3 text-center border-r border-slate-200">
                        <StatusBubble 
                          status="ip" 
                          active={rows[activeIdx].ip} 
                          disabled={true}
                        />
                      </div>
                      <div className="p-3 text-center border-r border-slate-200">
                        <StatusBubble 
                          status="ex" 
                          active={rows[activeIdx].ex} 
                          disabled={isCurrentYearLocked}
                          onClick={() => updateActive(r => ({ ...r, ex: !r.ex }))}
                        />
                      </div>
                      <div className="p-3 text-center border-r border-slate-200">
                        <StatusBubble 
                          status="den" 
                          active={rows[activeIdx].den} 
                          disabled={isCurrentYearLocked || !rows[activeIdx].ex}
                          onClick={() => rows[activeIdx].ex && updateActive(r => ({ ...r, den: !r.den }))}
                        />
                      </div>
                      <div className="p-3 text-center border-r border-slate-200">
                        <StatusBubble 
                          status="met" 
                          active={rows[activeIdx].met} 
                          disabled={isCurrentYearLocked || rows[activeIdx].ex}
                          onClick={() => !rows[activeIdx].ex && updateActive(r => ({ ...r, met: !r.met, exc: false, not: false }))}
                        />
                      </div>
                      <div className="p-3 text-center border-r border-slate-200">
                        <StatusBubble 
                          status="exc" 
                          active={rows[activeIdx].exc} 
                          disabled={isCurrentYearLocked || rows[activeIdx].ex}
                          onClick={() => !rows[activeIdx].ex && updateActive(r => ({ ...r, exc: !r.exc, met: false, not: false }))}
                        />
                      </div>
                      <div className="p-3 text-center">
                        <StatusBubble 
                          status="not" 
                          active={rows[activeIdx].not} 
                          disabled={isCurrentYearLocked || rows[activeIdx].ex}
                          onClick={() => !rows[activeIdx].ex && updateActive(r => ({ ...r, not: !r.not, met: false, exc: false }))}
                        />
                      </div>
                    </div>
                  </div>
                  </div>
              </div>

                <Separator className="my-6" />

                {/* Notes & Evidence Section */}
              <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-3">NOTES / EVIDENCE</h3>
                <RichTextEditor
                  value={rows[activeIdx].notesRich || ''}
                  onChange={(html) => updateActive(r => ({ ...r, notesRich: html }))}
                    placeholder="Add comments and notes..."
                />

                  {/* Evidence Upload - Using Dropzone Component */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="text-sm font-medium mb-3">Upload evidence/screenshots</div>
                {isFeatureEnabled('EVIDENCE_FILES_ENABLED') ? (
                  <>
                    <Dropzone
                      onFiles={(files) => onEvidenceUploadRowFiles(rows[activeIdx!].encounterId || rows[activeIdx!].pid, files)}
                      onError={(msg) => toast({ title: "Upload error", description: msg })}
                      accept={["image/*", ".pdf", ".csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]}
                    />
                    {rows[activeIdx].evidence && rows[activeIdx].evidence!.length > 0 && (
                      <div className="grid grid-cols-2 gap-3 mt-4">
                    {rows[activeIdx].evidence!.map((ev) => (
                          <div key={`${ev.name}-${ev.uploadedAt}`} className="rounded-lg border border-slate-200 p-3 bg-white">
                        {ev.type.startsWith("image/") ? (
                          <img src={ev.dataUrl} alt={`Evidence ${ev.name} for ${id}`} className="h-20 w-full rounded object-cover" loading="lazy" />
                        ) : (
                              <div className="text-sm">
                            <p className="font-medium truncate" title={ev.name}>{ev.name}</p>
                                <p className="text-slate-500 text-xs">{Math.round(ev.size / 1024)} KB • PDF</p>
                          </div>
                        )}
                            <div className="mt-2 flex items-center gap-2">
                              <Button asChild size="sm" variant="outline" className="text-xs">
                            <a href={ev.dataUrl} target="_blank" rel="noreferrer">View</a>
                          </Button>
                              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-500 text-xs" onClick={() => removeEvidenceRow(rows[activeIdx!].encounterId || rows[activeIdx!].pid, ev.name, ev.uploadedAt)}>
                                <X className="h-3 w-3" />
                              </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                  </>
                ) : (
                  <Alert>
                    <InfoIcon className="h-4 w-4" />
                    <AlertDescription>
                      Evidence file uploads are temporarily disabled.
                    </AlertDescription>
                  </Alert>
                )}
                  </div>
              </div>

                <Separator className="my-6" />

                {/* Activity Log Section */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-3">ACTIVITY LOG</h3>
                  <div className="bg-slate-50 rounded-lg p-4">
                    {activityLog.length > 0 ? (
                      <>
                        <div className="space-y-2 text-sm text-slate-600">
                          {(showAllActivityLog ? activityLog : activityLog.slice(0, 5)).map((entry, index) => {
                          const getIcon = (changeType: string) => {
                            switch (changeType) {
                              case 'manual_edit': return RefreshCcw;
                              case 'system_update': return Upload;
                              case 'original': return Download;
                              default: return RefreshCcw;
                            }
                          };
                          
                          const Icon = getIcon(entry.change_type);
                          const formatDate = (timestamp: string) => {
                            return new Date(timestamp).toLocaleString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            });
                          };
                          
                          return (
                            <div key={index} className="flex items-center gap-2">
                              <Icon className="h-3 w-3 text-slate-500" />
                              <span className="font-medium">Version {entry.version}</span>
                              <span>•</span>
                              <span>{entry.change_description || (entry.change_type === 'manual_edit' ? 'Manual edit' : entry.change_type === 'system_update' ? 'System update' : 'System import')} by {entry.changed_by}</span>
                              <span>•</span>
                              <span className="text-slate-400">{formatDate(entry.change_timestamp)}</span>
                            </div>
                          );
                        })}
                        </div>
                        {activityLog.length > 5 && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="flex flex-col items-center gap-2">
                              <p className="text-xs text-slate-500 text-center">
                                {showAllActivityLog 
                                  ? `Showing all ${activityLog.length} changes` 
                                  : `Showing 5 most recent of ${activityLog.length} total changes`
                                }
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowAllActivityLog(!showAllActivityLog)}
                                className="text-xs h-auto py-1"
                              >
                                {showAllActivityLog ? 'Show less' : 'View all changes'}
                              </Button>
                            </div>
          </div>
          )}
                      </>
                    ) : (
                      <div className="text-sm text-slate-500 text-center py-4">
                        No activity history available
                      </div>
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>

        </SheetContent>
      </Sheet>

      <AlertDialog open={warnOpen} onOpenChange={setWarnOpen}>
        <AlertDialogContent className="z-50">
          <AlertDialogHeader>
            <AlertDialogTitle>Make changes to measure outcomes?</AlertDialogTitle>
            <AlertDialogDescription>
              Changes you make here are saved immediately and will affect performance calculations for this measure. This notice will only appear once for {id}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setPendingToggle(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { localStorage.setItem(ackKey, '1'); if (pendingToggle) applyToggle(pendingToggle.idx, pendingToggle.field); setPendingToggle(null); setWarnOpen(false); }}>I understand</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
